import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Search,
  MapPin,
  Phone,
  Globe,
  Bell,
  Receipt,
  Coffee,
  MessageSquarePlus,
  X,
  UtensilsCrossed,
  Sparkles,
  Flame,
  Leaf,
  BadgePercent,
  ChevronRight,
  CakeSlice,
  Pizza,
  Beef,
  Salad,
  Soup,
  Sandwich,
  IceCream,
  Wine,
  GlassWater,
  Croissant,
  Cookie,
  ChefHat,
} from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { toast } from "sonner";
import { getPreset } from "../lib/themes";
import { externalLinkProps } from "../lib/url";
import { useCart, AddToCartButton, CartDrawer, ItemPickerDialog } from "../components/Cart";

/**
 * Premium tag styling: colored icon + subtle tinted background.
 * Kept theme-agnostic (no hard theme dependency) so it reads on any preset.
 */
const TAG_META = {
  popular: { icon: Sparkles, className: "bg-amber-500/15 text-amber-600 dark:text-amber-400 ring-1 ring-inset ring-amber-500/25" },
  new: { icon: Sparkles, className: "bg-sky-500/15 text-sky-600 dark:text-sky-400 ring-1 ring-inset ring-sky-500/25" },
  offer: { icon: BadgePercent, className: "bg-rose-500/15 text-rose-600 dark:text-rose-400 ring-1 ring-inset ring-rose-500/25" },
  vegan: { icon: Leaf, className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 ring-1 ring-inset ring-emerald-500/25" },
  spicy: { icon: Flame, className: "bg-orange-500/15 text-orange-600 dark:text-orange-400 ring-1 ring-inset ring-orange-500/25" },
};

/**
 * Infer a lucide icon for a category based on its name (multilingual keywords).
 * Falls back to a generic utensils icon.
 */
function categoryIconFor(name) {
  const n = (name || "").toLowerCase();
  if (/breakfast|frühstück|kahvaltı|kahvalti/.test(n)) return Croissant;
  if (/coffee|kaffee|kahve|tea|çay|cay/.test(n)) return Coffee;
  if (/burger|beef|steak|et|meat|kebab|köfte|kofte/.test(n)) return Beef;
  if (/pizza|pide|lahmacun/.test(n)) return Pizza;
  if (/salad|salat|salata/.test(n)) return Salad;
  if (/soup|suppe|çorba|corba/.test(n)) return Soup;
  if (/sandwich|toast|wrap|dürüm|durum/.test(n)) return Sandwich;
  if (/dessert|nachtisch|tatlı|tatli|kuchen|cake/.test(n)) return CakeSlice;
  if (/ice ?cream|eis|dondurma|sorbet/.test(n)) return IceCream;
  if (/cookie|keks|kurabiye|biscuit/.test(n)) return Cookie;
  if (/wine|wein|şarap|sarap/.test(n)) return Wine;
  if (/drink|getränk|getranke|içecek|icecek|water|wasser|su|soft|cola|juice|saft|meyve suyu/.test(n)) return GlassWater;
  if (/special|signature|chef|şef|sef|önerisi|onerisi/.test(n)) return ChefHat;
  if (/main|haupt|ana yemek|ana yemekler/.test(n)) return UtensilsCrossed;
  return UtensilsCrossed;
}

// Returns readable foreground (black or white) for a given hex bg color.
function getReadableTextColor(hex) {
  const h = (hex || "#a855f7").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 140 ? "#111827" : "#ffffff";
}

// Owner-defined informational banner shown above category items (and above the
// first item of that category in the 'Alle' view).
function CategoryInfoBox({ category, radius }) {
  const hasContent = category?.info_title || category?.info_text;
  if (!hasContent) return null;
  return (
    <div
      className="px-4 py-3 border-l-4 shadow-sm qm-fade-in"
      style={{
        borderRadius: radius,
        borderLeftColor: "var(--menu-primary)",
        backgroundColor: "color-mix(in srgb, var(--menu-primary) 8%, var(--menu-card-bg))",
        color: "var(--menu-text)",
      }}
      data-testid={`category-info-box-${category.id}`}
    >
      <div className="flex items-start gap-3">
        {category.info_icon && (
          <span className="text-2xl leading-none flex-shrink-0" aria-hidden>
            {category.info_icon}
          </span>
        )}
        <div className="min-w-0 flex-1">
          {category.info_title && (
            <div className="font-bold text-base leading-tight" style={{ color: "var(--menu-primary)" }}>
              {category.info_title}
            </div>
          )}
          {category.info_text && (
            <div className="text-sm whitespace-pre-wrap opacity-90 mt-1 leading-relaxed">
              {category.info_text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Elegant section header shown before each category group when the user is on "All". */
function SectionHeader({ title, itemCount }) {
  return (
    <div className="flex items-end justify-between gap-4 pt-3">
      <div className="min-w-0 flex-1">
        <h2
          className="qm-section-title text-2xl sm:text-3xl font-semibold leading-tight truncate"
          style={{ color: "var(--menu-text)" }}
        >
          {title}
        </h2>
        <div
          className="h-px mt-2 w-full"
          style={{
            background: "linear-gradient(90deg, var(--menu-primary), transparent)",
            opacity: 0.55,
          }}
        />
      </div>
      {typeof itemCount === "number" && (
        <span
          className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
          style={{
            backgroundColor: "color-mix(in srgb, var(--menu-primary) 12%, transparent)",
            color: "var(--menu-primary)",
          }}
        >
          {itemCount}
        </span>
      )}
    </div>
  );
}

/** Placeholder shown when an item has no image — theme-aware, elegant. */
function ImagePlaceholder({ name }) {
  const initial = (name || "•").trim().charAt(0).toUpperCase();
  return (
    <div
      className="w-full h-full grid place-items-center"
      style={{
        background:
          "linear-gradient(135deg, color-mix(in srgb, var(--menu-primary) 25%, transparent), color-mix(in srgb, var(--menu-accent) 25%, transparent))",
      }}
      aria-hidden
    >
      <div
        className="text-4xl font-bold opacity-50"
        style={{ color: "var(--menu-text)", fontFamily: "var(--menu-heading-font)" }}
      >
        {initial}
      </div>
    </div>
  );
}

export default function PublicMenu() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const tableId = searchParams.get("table");
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [activeTag, setActiveTag] = useState(null);
  const [callOpen, setCallOpen] = useState(false);
  const [customNote, setCustomNote] = useState("");
  const [callSending, setCallSending] = useState(false);
  const cart = useCart();
  const [pickerItem, setPickerItem] = useState(null);

  useEffect(() => {
    api.get(`/public/menu/${slug}`).then((r) => setData(r.data)).catch(() => setError("Not found"));
    api.post(`/public/menu/${slug}/track`, { type: "scan", table_id: tableId }).catch(() => {});
  }, [slug, tableId]);

  const trackClick = (itemId) => {
    api.post(`/public/menu/${slug}/track`, { type: "click", item_id: itemId, table_id: tableId }).catch(() => {});
  };

  const symbol = useMemo(() => {
    const c = data?.restaurant?.currency || "EUR";
    return { EUR: "€", USD: "$", TRY: "₺", AED: "AED" }[c];
  }, [data]);

  const filteredItems = useMemo(() => {
    if (!data) return [];
    return data.items.filter((it) => {
      if (activeCat !== "all" && it.category_id !== activeCat) return false;
      if (activeTag && !it.tags.includes(activeTag)) return false;
      if (
        search &&
        !it.name.toLowerCase().includes(search.toLowerCase()) &&
        !it.description?.toLowerCase().includes(search.toLowerCase())
      )
        return false;
      return true;
    });
  }, [data, search, activeCat, activeTag]);

  // Split filtered items into groups by category when viewing "All" — so we can
  // render an elegant section header per category. Must sit above early returns
  // to keep hook order stable across renders.
  const grouped = useMemo(() => {
    if (!data || activeCat !== "all") return null;
    const map = new Map();
    filteredItems.forEach((it) => {
      if (!map.has(it.category_id)) map.set(it.category_id, []);
      map.get(it.category_id).push(it);
    });
    return data.categories
      .map((c) => ({ category: c, items: map.get(c.id) || [] }))
      .filter((g) => g.items.length > 0);
  }, [activeCat, filteredItems, data]);

  if (error)
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="text-muted-foreground">{error}</div>
      </div>
    );
  if (!data) return <div className="min-h-screen grid place-items-center">{t("common.loading")}</div>;

  const sendCall = async (type, note = "") => {
    setCallSending(true);
    try {
      await api.post(`/public/menu/${slug}/service-request`, { type, table_id: tableId || null, note });
      toast.success(lang === "de" ? "Wir kommen gleich!" : lang === "tr" ? "Hemen geliyoruz!" : "We're on our way!");
      setCallOpen(false);
      setCustomNote("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setCallSending(false);
    }
  };

  // Service request is only available if owner is on Pro+ — backend gates it,
  // but we also hide the button if the restaurant plan is not Pro+ to avoid confusion
  const serviceEnabled = ["pro", "premium", "agency"].includes(data.plan) || data.plan === "admin";

  const r = data.restaurant;
  const theme = r.theme || {};
  // If a theme preset is set, derive all visual tokens from it (overrides individual color fields)
  const preset = theme.preset && theme.preset !== "default" ? getPreset(theme.preset) : null;
  // Auto-derive card bg + border from menu bg (lighter for dark themes, white for light themes)
  const bgIsDark = (() => {
    const bg = preset ? preset.bg : theme.background_color || "#FAFAFA";
    const hex = bg.replace("#", "");
    if (hex.length < 6) return false;
    const r1 = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return r1 * 0.299 + g * 0.587 + b * 0.114 < 128;
  })();
  const styleVars = preset
    ? {
        "--menu-primary": preset.primary,
        "--menu-accent": preset.accent,
        "--menu-bg": preset.bg,
        "--menu-text": preset.text,
        "--menu-card-bg": preset.surface,
        "--menu-border": preset.border,
        "--menu-muted": preset.muted,
        "--menu-heading-font": preset.fontHeading,
        "--menu-body-font": preset.fontBody,
        "--menu-radius": preset.radius,
        "--menu-btn-radius": preset.btnRadius,
      }
    : {
        "--menu-primary": theme.primary_color || "#6366F1",
        "--menu-accent": theme.accent_color || "#A855F7",
        "--menu-bg": theme.background_color || "#FAFAFA",
        "--menu-text": theme.text_color || "#09090B",
        "--menu-card-bg": bgIsDark ? "rgba(255,255,255,0.06)" : "#ffffff",
        "--menu-border": bgIsDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
        "--menu-muted": bgIsDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)",
        "--menu-heading-font": `'${theme.font_family || "Outfit"}', sans-serif`,
        "--menu-body-font": `'${theme.font_family || "Outfit"}', sans-serif`,
        "--menu-radius": "1rem",
        "--menu-btn-radius":
          theme.button_style === "pill" ? "9999px" : theme.button_style === "sharp" ? "0" : "12px",
      };
  const radius = styleVars["--menu-btn-radius"];
  const cardRadius = "1.25rem"; // premium consistent radius for cards regardless of button style

  // Precompute "from/ab" label once for variant prices
  const fromLabel =
    lang === "de" ? "ab" : lang === "tr" ? "başlangıç" : "from";

  return (
    <div
      className="min-h-screen pb-28 qm-themed"
      style={{
        ...styleVars,
        backgroundColor: "var(--menu-bg)",
        color: "var(--menu-text)",
        fontFamily: "var(--menu-body-font)",
      }}
      data-testid="public-menu"
    >
      {/* ===== HERO ===== */}
      <div
        className="relative h-64 sm:h-80 md:h-[22rem] overflow-hidden"
        style={{
          background: r.cover_image
            ? undefined
            : `linear-gradient(135deg, var(--menu-primary), var(--menu-accent))`,
        }}
      >
        {r.cover_image && (
          <img
            src={r.cover_image}
            alt=""
            className="w-full h-full object-cover qm-card-img"
            loading="eager"
          />
        )}
        {/* dual gradient — top for lang switcher legibility, bottom for text */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70" />
        {bgIsDark && <div className="absolute inset-0 qm-hero-grain" />}
        <div className="absolute top-3 right-3 z-10">
          <LanguageSwitcher />
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white">
          <div className="max-w-4xl mx-auto flex items-end gap-4">
            {r.logo && (
              <img
                src={r.logo}
                alt="logo"
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover shadow-2xl ring-1 ring-white/20"
                style={{ boxShadow: "0 10px 30px -10px rgba(0,0,0,0.55)" }}
              />
            )}
            <div className="flex-1 min-w-0">
              <h1 className="qm-hero-title text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.05]">
                {r.name}
              </h1>
              {r.description && (
                <p className="text-sm sm:text-base opacity-90 mt-1.5 line-clamp-2 max-w-xl">
                  {r.description}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ===== INFO BAR — floating glass card overlapping hero ===== */}
      {(r.address || r.phone || r.website) && (
        <div className="px-3 sm:px-4 -mt-6 relative z-10">
          <div
            className="max-w-4xl mx-auto flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 backdrop-blur-md border shadow-lg qm-fade-in"
            style={{
              backgroundColor: bgIsDark
                ? "color-mix(in srgb, var(--menu-card-bg) 92%, transparent)"
                : "rgba(255,255,255,0.85)",
              borderColor: "var(--menu-border)",
              borderRadius: cardRadius,
              color: "var(--menu-text)",
            }}
          >
            {r.address && (
              <span className="inline-flex items-center gap-1.5 text-xs sm:text-[13px]">
                <span
                  className="grid place-items-center w-6 h-6 rounded-lg"
                  style={{
                    backgroundColor: "color-mix(in srgb, var(--menu-primary) 15%, transparent)",
                    color: "var(--menu-primary)",
                  }}
                  aria-hidden
                >
                  <MapPin className="h-3.5 w-3.5" />
                </span>
                <span className="opacity-80">{r.address}</span>
              </span>
            )}
            {r.phone && (
              <a
                href={`tel:${r.phone}`}
                className="inline-flex items-center gap-1.5 text-xs sm:text-[13px] hover:opacity-100"
              >
                <span
                  className="grid place-items-center w-6 h-6 rounded-lg"
                  style={{
                    backgroundColor: "color-mix(in srgb, var(--menu-primary) 15%, transparent)",
                    color: "var(--menu-primary)",
                  }}
                  aria-hidden
                >
                  <Phone className="h-3.5 w-3.5" />
                </span>
                <span className="opacity-80">{r.phone}</span>
              </a>
            )}
            {r.website && (
              <a
                {...externalLinkProps(r.website)}
                className="inline-flex items-center gap-1.5 text-xs sm:text-[13px] hover:opacity-100"
                data-testid="public-menu-website-link"
              >
                <span
                  className="grid place-items-center w-6 h-6 rounded-lg"
                  style={{
                    backgroundColor: "color-mix(in srgb, var(--menu-primary) 15%, transparent)",
                    color: "var(--menu-primary)",
                  }}
                  aria-hidden
                >
                  <Globe className="h-3.5 w-3.5" />
                </span>
                <span className="opacity-80">Website</span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* ===== SEARCH ===== */}
      <div className="px-3 sm:px-4 mt-4">
        <div className="max-w-4xl mx-auto relative">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4"
            style={{ color: "var(--menu-muted)" }}
          />
          <input
            type="text"
            placeholder={t("publicMenu.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 h-12 border outline-none text-[15px] transition-shadow focus:shadow-lg"
            style={{
              backgroundColor: "var(--menu-card-bg)",
              borderColor: "var(--menu-border)",
              color: "var(--menu-text)",
              borderRadius: cardRadius,
            }}
            data-testid="public-search"
          />
        </div>
      </div>

      {/* ===== CATEGORIES ===== */}
      <div className="mt-5 px-3 sm:px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1">
            <CategoryChip
              active={activeCat === "all"}
              onClick={() => setActiveCat("all")}
              label={t("common.all")}
              testId="cat-filter-all"
              icon={<UtensilsCrossed className="h-3.5 w-3.5" />}
            />
            {data.categories.map((c) => {
              const IconCmp = categoryIconFor(c.name);
              return (
                <CategoryChip
                  key={c.id}
                  active={activeCat === c.id}
                  onClick={() => setActiveCat(c.id)}
                  label={c.name}
                  testId={`cat-filter-${c.id}`}
                  icon={
                    c.info_icon ? (
                      <span className="text-sm leading-none">{c.info_icon}</span>
                    ) : (
                      <IconCmp className="h-3.5 w-3.5" />
                    )
                  }
                />
              );
            })}
          </div>

          {/* Tag filters */}
          <div className="mt-1 flex gap-2 pb-2 overflow-x-auto scrollbar-hide -mx-1 px-1">
            {["popular", "new", "offer", "vegan"].map((tg) => {
              const isActive = activeTag === tg;
              const Meta = TAG_META[tg];
              const Icon = Meta?.icon;
              return (
                <button
                  key={tg}
                  onClick={() => setActiveTag(isActive ? null : tg)}
                  className="qm-chip inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border whitespace-nowrap"
                  style={{
                    background: isActive ? "var(--menu-primary)" : "transparent",
                    borderColor: "var(--menu-primary)",
                    color: isActive ? "#ffffff" : "var(--menu-primary)",
                    borderRadius: "9999px",
                  }}
                  data-testid={`tag-filter-${tg}`}
                  data-active={isActive ? "true" : "false"}
                  aria-pressed={isActive}
                >
                  {Icon && <Icon className="h-3 w-3" />}
                  {t(`menu.tag${tg.charAt(0).toUpperCase() + tg.slice(1)}`)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== ITEMS ===== */}
      <div className="px-3 sm:px-4 mt-2">
        <div className="max-w-4xl mx-auto space-y-3">
          {/* Info box for single-category view */}
          {activeCat !== "all" &&
            (() => {
              const cat = data.categories.find((c) => c.id === activeCat);
              return cat ? <CategoryInfoBox category={cat} radius={cardRadius} /> : null;
            })()}

          {/* Grouped "All" view: elegant section headers per category */}
          {grouped ? (
            grouped.map(({ category, items }) => (
              <div key={category.id} className="space-y-3">
                <SectionHeader title={category.name} itemCount={items.length} />
                <CategoryInfoBox category={category} radius={cardRadius} />
                {items.map((it) => (
                  <MenuItemCard
                    key={it.id}
                    item={it}
                    symbol={symbol}
                    fromLabel={fromLabel}
                    cardRadius={cardRadius}
                    orderingEnabled={data.ordering_enabled}
                    onClickCard={() => trackClick(it.id)}
                    onAdd={(item, needsPicker) => {
                      if (needsPicker) setPickerItem(item);
                      else {
                        cart.addLine(item, { qty: 1 });
                        toast.success(item.name, { description: `+1 ${t("cart.addToCart").toLowerCase()}` });
                      }
                    }}
                    t={t}
                  />
                ))}
              </div>
            ))
          ) : (
            filteredItems.map((it) => (
              <MenuItemCard
                key={it.id}
                item={it}
                symbol={symbol}
                fromLabel={fromLabel}
                cardRadius={cardRadius}
                orderingEnabled={data.ordering_enabled}
                onClickCard={() => trackClick(it.id)}
                onAdd={(item, needsPicker) => {
                  if (needsPicker) setPickerItem(item);
                  else {
                    cart.addLine(item, { qty: 1 });
                    toast.success(item.name, { description: `+1 ${t("cart.addToCart").toLowerCase()}` });
                  }
                }}
                t={t}
              />
            ))
          )}

          {filteredItems.length === 0 && (
            <div className="text-center py-16 opacity-60">
              <UtensilsCrossed
                className="h-10 w-10 mx-auto mb-3 opacity-40"
                style={{ color: "var(--menu-primary)" }}
              />
              <div className="text-sm">No items</div>
            </div>
          )}
        </div>
      </div>

      {/* Watermark */}
      {data.show_watermark && (
        <div
          className="fixed bottom-4 left-4 text-[11px] px-3 py-1.5 rounded-full backdrop-blur-md border shadow-lg z-30 inline-flex items-center gap-1.5"
          style={{
            backgroundColor: "color-mix(in srgb, var(--menu-card-bg) 90%, transparent)",
            borderColor: "var(--menu-border)",
            color: "var(--menu-text)",
          }}
          data-testid="watermark"
        >
          <Sparkles className="h-3 w-3" style={{ color: "var(--menu-primary)" }} />
          <span className="opacity-80">{t("publicMenu.poweredBy")}</span>
          <span className="font-bold gradient-text">qrmenu-pro.com</span>
        </div>
      )}

      {/* Floating Call-Waiter button (only when restaurant has Pro+ plan) */}
      {serviceEnabled && (
        <button
          onClick={() => setCallOpen(true)}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 px-5 py-3 rounded-full shadow-2xl font-bold text-white transition-transform hover:scale-105 active:scale-95"
          style={{ background: `linear-gradient(135deg, var(--menu-primary), var(--menu-accent))` }}
          aria-label="Call service"
          data-testid="call-service-btn"
        >
          <Bell className="h-5 w-5" />
          <span className="text-sm">
            {lang === "de" ? "Service" : lang === "tr" ? "Çağır" : "Call"}
          </span>
        </button>
      )}

      {/* Call modal */}
      {callOpen && (
        <div
          className="fixed inset-0 z-50 grid place-items-end sm:place-items-center bg-black/50 backdrop-blur-sm"
          onClick={() => setCallOpen(false)}
          data-testid="call-modal-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl"
            style={{
              backgroundColor: "var(--menu-card-bg)",
              color: "var(--menu-text)",
              border: `1px solid var(--menu-border)`,
            }}
            data-testid="call-modal"
          >
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold">
                {lang === "de"
                  ? "Wie können wir helfen?"
                  : lang === "tr"
                  ? "Nasıl yardım edebiliriz?"
                  : "How can we help?"}
              </h2>
              <button
                onClick={() => setCallOpen(false)}
                className="opacity-60 hover:opacity-100"
                aria-label="Close"
                data-testid="call-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {tableId && (
              <div className="text-xs opacity-70 mb-4">
                {lang === "de" ? "Tisch" : lang === "tr" ? "Masa" : "Table"}:{" "}
                <span className="font-bold">{tableId}</span>
              </div>
            )}
            <div className="grid grid-cols-1 gap-2.5">
              <button
                disabled={callSending}
                onClick={() => sendCall("waiter")}
                className="flex items-center gap-3 p-4 rounded-2xl text-left border transition-colors hover:opacity-80 disabled:opacity-50"
                style={{ borderColor: "var(--menu-border)", backgroundColor: "var(--menu-card-bg)" }}
                data-testid="call-waiter"
              >
                <div
                  className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
                  style={{ background: "var(--menu-primary)", color: "white" }}
                >
                  <Bell className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-sm">
                    {lang === "de" ? "Kellner rufen" : lang === "tr" ? "Garson çağır" : "Call waiter"}
                  </div>
                  <div className="text-xs opacity-60">
                    {lang === "de" ? "Bestellung aufgeben" : lang === "tr" ? "Sipariş için" : "To take order"}
                  </div>
                </div>
              </button>
              <button
                disabled={callSending}
                onClick={() => sendCall("bill")}
                className="flex items-center gap-3 p-4 rounded-2xl text-left border transition-colors hover:opacity-80 disabled:opacity-50"
                style={{ borderColor: "var(--menu-border)" }}
                data-testid="call-bill"
              >
                <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                  <Receipt className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-sm">
                    {lang === "de" ? "Rechnung bitte" : lang === "tr" ? "Hesap, lütfen" : "Bill, please"}
                  </div>
                  <div className="text-xs opacity-60">
                    {lang === "de" ? "Zahlung anfordern" : lang === "tr" ? "Ödeme için" : "Ready to pay"}
                  </div>
                </div>
              </button>
              <button
                disabled={callSending}
                onClick={() => sendCall("water")}
                className="flex items-center gap-3 p-4 rounded-2xl text-left border transition-colors hover:opacity-80 disabled:opacity-50"
                style={{ borderColor: "var(--menu-border)" }}
                data-testid="call-water"
              >
                <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-blue-500/20 text-blue-700 dark:text-blue-400">
                  <Coffee className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-sm">
                    {lang === "de"
                      ? "Wasser / Getränk"
                      : lang === "tr"
                      ? "Su / İçecek"
                      : "Water / drink"}
                  </div>
                  <div className="text-xs opacity-60">
                    {lang === "de" ? "Nachschub bitte" : lang === "tr" ? "Tazeleme" : "Refill please"}
                  </div>
                </div>
              </button>
              <div className="rounded-2xl border p-3 space-y-2" style={{ borderColor: "var(--menu-border)" }}>
                <div className="flex items-center gap-2 text-xs font-semibold">
                  <MessageSquarePlus className="h-3.5 w-3.5" />
                  {lang === "de" ? "Eigene Nachricht" : lang === "tr" ? "Özel mesaj" : "Custom note"}
                </div>
                <input
                  type="text"
                  value={customNote}
                  onChange={(e) => setCustomNote(e.target.value)}
                  placeholder={
                    lang === "de"
                      ? "z. B. extra Servietten"
                      : lang === "tr"
                      ? "örn. ekstra peçete"
                      : "e.g. extra napkins"
                  }
                  className="w-full h-10 px-3 rounded-xl border bg-transparent text-sm"
                  style={{ borderColor: "var(--menu-border)" }}
                  maxLength={120}
                  data-testid="call-custom-note"
                />
                <button
                  disabled={callSending || !customNote.trim()}
                  onClick={() => sendCall("custom", customNote)}
                  className="w-full h-10 rounded-xl text-sm font-bold text-white disabled:opacity-40"
                  style={{ background: "var(--menu-primary)" }}
                  data-testid="call-custom-send"
                >
                  {lang === "de" ? "Senden" : lang === "tr" ? "Gönder" : "Send"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cart / ordering (Premium plan feature) */}
      {data.ordering_enabled && (
        <>
          <CartDrawer
            cart={cart}
            slug={slug}
            tableId={tableId}
            tableLabel={null}
            symbol={symbol}
            primary={data.restaurant?.theme?.primary_color || "var(--menu-primary)"}
            currency={data.restaurant?.currency || "EUR"}
            orderingEnabled={data.ordering_enabled}
          />
          {pickerItem && (
            <ItemPickerDialog
              item={pickerItem}
              symbol={symbol}
              primary={data.restaurant?.theme?.primary_color || "#a855f7"}
              onClose={() => setPickerItem(null)}
              onConfirm={(opts) => {
                cart.addLine(pickerItem, opts);
                toast.success(pickerItem.name, {
                  description: `+${opts.qty} ${t("cart.addToCart").toLowerCase()}`,
                });
                setPickerItem(null);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

/** Category chip — pill with optional icon, active state uses theme primary bg + soft glow. */
function CategoryChip({ active, onClick, label, testId, icon }) {
  return (
    <button
      onClick={onClick}
      className="qm-chip inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border whitespace-nowrap"
      style={{
        background: active ? "var(--menu-primary)" : "transparent",
        borderColor: active ? "var(--menu-primary)" : "var(--menu-border)",
        color: active ? "#ffffff" : "var(--menu-text)",
        borderRadius: "9999px",
        boxShadow: active ? "0 6px 20px -6px color-mix(in srgb, var(--menu-primary) 55%, transparent)" : "none",
      }}
      data-testid={testId}
    >
      {icon}
      {label}
    </button>
  );
}

/**
 * Premium menu item card — horizontal layout with a big left image, elegant typography,
 * accent-colored price and a floating "+" action button.
 * Fully theme-aware via CSS variables. All data-testids preserved.
 */
function MenuItemCard({
  item: it,
  symbol,
  fromLabel,
  cardRadius,
  orderingEnabled,
  onClickCard,
  onAdd,
  t,
}) {
  const hasVariants = it.variants && it.variants.length > 0;
  return (
    <div
      onClick={onClickCard}
      className="qm-card qm-fade-in relative overflow-hidden border cursor-pointer"
      style={{
        backgroundColor: "var(--menu-card-bg)",
        borderColor: "var(--menu-border)",
        color: "var(--menu-text)",
        borderRadius: cardRadius,
        opacity: it.available ? 1 : 0.55,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -18px rgba(0,0,0,0.25)",
      }}
      data-testid={`public-item-${it.id}`}
    >
      <div className="flex gap-3 sm:gap-5 p-2.5 sm:p-4">
        {/* Image / Placeholder */}
        <div
          className="qm-product-media relative shrink-0 overflow-hidden"
          style={{ borderRadius: "1rem" }}
        >
          {it.image ? (
            <img
              src={it.image}
              alt={it.name}
              className="w-full h-full object-cover qm-card-img"
              loading="lazy"
            />
          ) : (
            <ImagePlaceholder name={it.name} />
          )}
          {!it.available && (
            <div className="absolute inset-0 bg-black/40 grid place-items-center">
              <span className="text-[10px] font-bold text-white uppercase tracking-wider">
                {t("menu.unavailable")}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex justify-between gap-3 items-start">
            <h3
              className="font-bold text-[15px] sm:text-base leading-tight flex-1 min-w-0"
              style={{ fontFamily: "var(--menu-heading-font)" }}
            >
              {it.name}
            </h3>
            <div className="text-right shrink-0">
              {hasVariants && (
                <div className="text-[10px] uppercase tracking-wider opacity-55 leading-none mb-0.5">
                  {fromLabel}
                </div>
              )}
              <span
                className="qm-price font-bold text-base sm:text-lg whitespace-nowrap"
                style={{ color: "var(--menu-primary)" }}
              >
                {it.price.toFixed(2)} {symbol}
              </span>
            </div>
          </div>

          {it.description && (
            <p className="text-[13px] leading-snug opacity-70 mt-1 line-clamp-2">
              {it.description}
            </p>
          )}

          {/* Badges row */}
          {(it.tags.length > 0 || (it.labels || []).length > 0) && (
            <div className="flex flex-wrap gap-1.5 mt-2 items-center">
              {it.tags.map((tg) => {
                const Meta = TAG_META[tg];
                const Icon = Meta?.icon;
                return (
                  <span
                    key={tg}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                      Meta?.className || "bg-secondary"
                    }`}
                  >
                    {Icon && <Icon className="h-2.5 w-2.5" />}
                    {t(`menu.tag${tg.charAt(0).toUpperCase() + tg.slice(1)}`)}
                  </span>
                );
              })}
              {(it.labels || []).map((lb, idx) => (
                <span
                  key={`lb-${idx}`}
                  className="px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ring-1 ring-inset ring-black/5"
                  style={{
                    backgroundColor: lb.color,
                    color: getReadableTextColor(lb.color),
                  }}
                  data-testid={`public-item-label-${it.id}-${idx}`}
                >
                  {lb.text}
                </span>
              ))}
            </div>
          )}

          {it.allergens?.length > 0 && (
            <div
              className="text-[10px] opacity-50 mt-2 pt-1.5 border-t"
              style={{ borderColor: "var(--menu-border)" }}
            >
              {t("publicMenu.contains")}: {it.allergens.join(", ")}
            </div>
          )}

          {/* Bottom row: variant hint chevron + add button */}
          <div className="mt-2 flex items-center justify-between gap-2">
            {hasVariants ? (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-semibold opacity-70"
                style={{ color: "var(--menu-primary)" }}
              >
                {t("cart.subtotal") === "Zwischensumme"
                  ? "Optionen wählen"
                  : t("cart.subtotal") === "Ara toplam"
                  ? "Seçenekleri seç"
                  : "Choose options"}
                <ChevronRight className="h-3 w-3" />
              </span>
            ) : (
              <span />
            )}
            {orderingEnabled && it.available && (
              <div className="qm-add-btn">
                <AddToCartButton
                  item={it}
                  primary="var(--menu-primary)"
                  testId={`add-to-cart-${it.id}`}
                  onOpenPicker={onAdd}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
